import { useCallback, useEffect, useState } from 'react'
import Bildlupe from '../komponenten/Bildlupe'
import ErkennungsKarte, { vorauswahl } from '../komponenten/Erkennung'
import Ladehinweis from '../komponenten/Ladehinweis'
import { ApiFehler } from '../lib/api'
import { useBild } from '../lib/bild'
import { HERKUNFT_TEXT } from '../lib/kennzahlen'
import { datum, useLaden } from '../lib/laden'
import type { BildErkennung } from '../lib/warteschlangen'
import {
  holeDurchsicht,
  holeOrtsErkennung,
  ortDurchgesehen,
  ortsErkennungErneut,
  type Durchsichtsort,
} from '../lib/ortsdurchsicht'

/**
 * Spielplätze mit Bildern durchsehen — was darauf zu sehen ist, an den Ort.
 *
 * **Keine Warteschlange.** Hier läuft nichts ab und niemand wartet: Es sind
 * 504 Orte mit 1.869 Bildern, von denen 140 kein einziges Merkmal tragen. Was
 * auf den Bildern steht, weiß bisher niemand.
 *
 * **Zwanzig je Seite**, und die Bilderkennung läuft beim Öffnen einer Seite
 * über genau diese zwanzig — im Schnitt rund 110 Bilder. Der ganze Bestand
 * auf einmal wäre lange Wartezeit und die Kosten in einem Schwung. Wer
 * weiterblättert, stößt die nächsten an; was schon erkannt ist, wird nicht
 * noch einmal gerechnet.
 *
 * **Die ärmsten Orte zuerst.** Wo schon siebzehn Merkmale stehen, sucht man
 * lange nach der einen Lücke; wo keines steht, zählt jedes Häkchen.
 *
 * **„Nichts zu ergänzen" ist eine Antwort**, keine übersprungene Zeile. Ohne
 * sie stünde derselbe Ort morgen wieder da, und die Liste ließe sich über
 * Wochen nicht abarbeiten.
 */
export default function Ortsdurchsicht() {
  const [seite, setSeite] = useState(0)
  const [nurOffene, setNurOffene] = useState(true)
  const [zustand] = useLaden(
    () => holeDurchsicht(seite, nurOffene),
    [seite, nurOffene],
  )
  // Nur einer offen: zwanzig Erkennungskarten gleichzeitig wären zwanzig
  // Katalogtabellen im Baum, und durchgesehen wird ohnehin nacheinander.
  const [offen, setOffen] = useState<string | null>(null)
  const [erledigt, setErledigt] = useState<Record<string, string>>({})

  useEffect(() => {
    setOffen(null)
    setErledigt({})
  }, [seite, nurOffene])

  // Beim Abarbeiten schrumpft die Grundmenge: Wer auf Seite 2 stand und die
  // vorletzten Orte erledigt hat, stünde vor einer Seite, die es nicht mehr
  // gibt. Geprüft wird gegen die **Seitenzahl**, nicht gegen die gemeldete
  // Seite — während des Nachladens steht noch die alte Antwort da, und ein
  // Vergleich mit deren Seitennummer machte jedes Weiterblättern rückgängig.
  const seiten = zustand.art === 'da' ? zustand.wert.seiten : null
  useEffect(() => {
    if (seiten !== null && seite >= seiten) setSeite(seiten - 1)
  }, [seiten, seite])

  return (
    <section className="stapel">
      <div className="stapel-kopf">
        <h1>Ortsdurchsicht</h1>
        {zustand.art === 'da' && (
          <p className="gedaempft">
            <span className="zahl">{zustand.wert.gesamt}</span> Orte mit Bildern ·{' '}
            <span className="zahl">
              {Math.max(0, zustand.wert.offen - Object.keys(erledigt).length)}
            </span>{' '}
            noch nicht durchgesehen
          </p>
        )}
      </div>

      <p className="leise">
        Die Bilderkennung läuft beim Öffnen dieser Seite über die Bilder dieser
        zwanzig Orte. Sie schlägt vor — entschieden wird von Hand. Ein Foto
        belegt nur, dass etwas <em>da</em> ist; „nicht zu sehen" heißt nie
        „gibt es nicht".
      </p>

      <div className="knopf-reihe">
        <label className="leise">
          <input
            type="checkbox"
            checked={nurOffene}
            onChange={(e) => {
              setSeite(0)
              setNurOffene(e.target.checked)
            }}
          />{' '}
          nur noch nicht durchgesehene
        </label>
      </div>

      <Ladehinweis zustand={zustand} />

      {zustand.art === 'da' && (
        <>
          {zustand.wert.eintraege.length === 0 ? (
            <p className="gedaempft">
              {nurOffene
                ? 'Alles durchgesehen. Das ist selten und gut.'
                : 'Keine Orte mit Bildern auf dieser Seite.'}
            </p>
          ) : (
            <ul className="paar-liste">
              {zustand.wert.eintraege.map((o) => (
                <OrtsKarte
                  key={o.id}
                  o={o}
                  offen={offen === o.id}
                  aufklappen={() => setOffen(offen === o.id ? null : o.id)}
                  fertig={(satz) => {
                    setErledigt((e) => ({ ...e, [o.id]: satz }))
                    setOffen(null)
                  }}
                  erledigt={erledigt[o.id] ?? null}
                />
              ))}
            </ul>
          )}

          <Blaetterleiste seite={seite} seiten={zustand.wert.seiten} blaettern={setSeite} />
        </>
      )}
    </section>
  )
}

function Blaetterleiste({
  seite,
  seiten,
  blaettern,
}: {
  seite: number
  seiten: number
  blaettern: (n: number) => void
}) {
  return (
    <div className="knopf-reihe blaetterleiste">
      <button
        className="knopf knopf-still"
        type="button"
        disabled={seite === 0}
        onClick={() => blaettern(seite - 1)}
      >
        ← Zurück
      </button>
      <span className="leise">
        Seite <span className="zahl">{seite + 1}</span> von{' '}
        <span className="zahl">{seiten}</span>
      </span>
      <button
        className="knopf knopf-still"
        type="button"
        disabled={seite + 1 >= seiten}
        onClick={() => blaettern(seite + 1)}
      >
        Weiter →
      </button>
    </div>
  )
}

function OrtsKarte({
  o,
  offen,
  aufklappen,
  fertig,
  erledigt,
}: {
  o: Durchsichtsort
  offen: boolean
  aufklappen: () => void
  fertig: (satz: string) => void
  erledigt: string | null
}) {
  const [gewaehlt, setzen] = useState<string[]>([])
  const [gross, setGross] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  // Vorgehakt wird **einmal**, sobald Ergebnisse da sind.
  //
  // Beim Aufklappen rechnet die Erkennung oft noch; die Karte lädt dann
  // selbst nach und meldet das Ergebnis über `beiErgebnis` hierher. Genau
  // einmal, weil ein zweiter Lauf sonst einen Haken zurücksetzte, den ein
  // Mensch gerade bewusst entfernt hat — und das Entfernen ist die halbe
  // Arbeit an dieser Liste.
  const [vorgewaehlt, setVorgewaehlt] = useState(false)
  const vorwaehlen = useCallback(
    (bilder: BildErkennung[]) => {
      setVorgewaehlt((schon) => {
        if (schon) return true
        setzen(vorauswahl(bilder, o.features))
        return true
      })
    },
    [o.features],
  )

  useEffect(() => {
    if (!offen || vorgewaehlt) return
    // Schon fertig erkannt: Dann kommt kein Nachladen mehr, das melden
    // könnte, und die Vorauswahl steht sofort.
    if (!o.bilder.some((b) => b.erkennung.zustand === 'laeuft')) vorwaehlen(o.bilder)
  }, [offen, vorgewaehlt, o.bilder, vorwaehlen])

  const nachladen = useCallback(
    async () => (await holeOrtsErkennung(o.id)).bilder,
    [o.id],
  )

  async function entscheide() {
    setLaeuft(true)
    setFehler(null)
    try {
      const a = await ortDurchgesehen(o.id, gewaehlt)
      fertig(
        a.merkmale.length === 0
          ? 'durchgesehen — nichts zu ergänzen'
          : `durchgesehen — ${a.merkmale.length} ${
              a.merkmale.length === 1 ? 'Merkmal' : 'Merkmale'
            } übernommen`,
      )
    } catch (f) {
      setFehler(f instanceof ApiFehler || f instanceof Error ? f.message : 'Unbekannter Fehler')
    } finally {
      setLaeuft(false)
    }
  }

  const adresse =
    [o.strasse, [o.plz, o.stadt].filter(Boolean).join(' ')].filter(Boolean).join(', ') ||
    'keine Adresse hinterlegt'

  return (
    <li className="karte paar">
      <div className="paar-kopf">
        <span className="mensch-name">
          <a
            href={`https://kinderspiel.app/p/${o.id}`}
            target="_blank"
            rel="noreferrer noopener"
          >
            {o.name ?? 'ohne Namen'}
          </a>
        </span>
        <span className="leise">
          <span className="zahl">{o.fotos}</span> {o.fotos === 1 ? 'Bild' : 'Bilder'} ·{' '}
          <span className="zahl">{o.merkmale}</span>{' '}
          {o.merkmale === 1 ? 'Merkmal' : 'Merkmale'}
        </span>
      </div>
      <p className="gedaempft">{adresse}</p>
      <p className="leise">
        {o.herkunft ? (HERKUNFT_TEXT[o.herkunft] ?? o.herkunft) : 'Quelle unbekannt'}
        {o.durchgesehen_at && ` · zuletzt durchgesehen ${datum(o.durchgesehen_at)}`}
      </p>

      {erledigt ? (
        <p className="meldung meldung-erfolg">{erledigt}</p>
      ) : !offen ? (
        <div className="knopf-reihe">
          <button className="knopf knopf-still" type="button" onClick={aufklappen}>
            Durchsehen …
          </button>
        </div>
      ) : (
        <>
          <div className="bilder-reihe">
            {o.bilder.map((b) => (
              <Vorschaubild key={b.pfad} pfad={b.pfad} anklicken={() => setGross(b.pfad)} />
            ))}
          </div>

          <ErkennungsKarte
            bilder={o.bilder}
            features={o.features}
            umfeld={o.accessibility}
            gewaehlt={gewaehlt}
            setzen={setzen}
            nachladen={nachladen}
            beiErgebnis={vorwaehlen}
            erneut={async () => {
              await ortsErkennungErneut(o.id)
            }}
          />

          {fehler && (
            <p className="meldung meldung-fehler" role="alert">
              {fehler}
            </p>
          )}

          <div className="knopf-reihe">
            <button
              className="knopf knopf-frei"
              type="button"
              disabled={laeuft}
              onClick={() => void entscheide()}
            >
              {laeuft
                ? 'Wird gespeichert …'
                : gewaehlt.length === 0
                  ? 'Nichts zu ergänzen'
                  : `${gewaehlt.length} ${
                      gewaehlt.length === 1 ? 'Merkmal' : 'Merkmale'
                    } übernehmen`}
            </button>
            <button className="knopf knopf-still" type="button" onClick={aufklappen}>
              Zuklappen
            </button>
          </div>

          {gross && <Bildlupe pfad={gross} schliessen={() => setGross(null)} />}
        </>
      )}
    </li>
  )
}

function Vorschaubild({ pfad, anklicken }: { pfad: string; anklicken: () => void }) {
  const bild = useBild(pfad)
  if (bild.art !== 'da') {
    return (
      <div className="bild-klein bild-klein-leer">
        <span className="leise">{bild.art === 'laedt' ? 'lädt …' : 'nicht abrufbar'}</span>
      </div>
    )
  }
  return (
    <button type="button" className="bild-knopf" onClick={anklicken} title="Groß ansehen">
      <img className="bild-klein" src={bild.adresse} alt="Bild des Spielplatzes" />
    </button>
  )
}
